<template>
  <v-row>
    <v-col cols="12">
      <div :style="{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }">
        <ServiceLogo :service="IApp.IFeedTypes.GoogleAnalytics" size="50px" />
        <h2 class="ml-2 title">Website Traffic Metrics</h2>
      </div>
    </v-col>
  </v-row>

  <!-- Total Metrics Charts -->
  <v-row>
    <v-col
      cols="12"
      md="12"
      lg="12"
      xl="4"
      v-for="item in totalItems"
      :key="item.value"
      :class="{ 'page-break': item.value === IApp.IGoogleAnalyticsAnalyticsTotalMetrics.Sessions }"
    >
      <v-card class="chart-card">
        <v-card-text>
          <ChartLazyLoader :loading="state.totalLoading">
            <template #default>
              <LineChart
                :series="totalAnalyticsChartData.find((d) => d.key === item.label)!.value.series"
                :xAxis="totalAnalyticsChartData.find((d) => d.key === item.label)!.value.xAxis"
                :title="item.label"
                class="no-axis-label-in-tooltip"
              />
            </template>
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <v-row>
    <!-- Top Viewed Pages Table -->
    <v-col cols="12" class="page-break">
      <v-card class="google-analytics-top-viewed-pages-table-card" :title="`Top viewed pages`">
        <div class="tw:hidden!" :data-loading="state.topViewedPagesDataLoading"></div>
        <v-data-table
          :headers="topViewedPagesTableData.headers"
          :items="topViewedPagesChart"
          item-value="name"
          density="comfortable"
          class="google-analytics-top-viewed-pages-table"
        >
          <template v-slot:item.views="{ item }">
            {{ formatNumber(item.views) }}
          </template>
          <template v-slot:item.viewsChange="{ item }">
            <span :class="item.viewsChange >= 0 ? 'success--text' : 'error--text'">
              {{ item.viewsChange >= 0 ? "▲" : "▼" }}
              {{ formatNumber(Math.abs(item.viewsChange) * 100) }}%
            </span>
          </template>
        </v-data-table>
      </v-card>
    </v-col>

    <!-- Session Count Table -->
    <v-col cols="12" class="page-break">
      <v-card
        class="google-analytics-session-count-table-card"
        :title="`Top Event/Traffic Sources`"
      >
        <div class="tw:hidden!" :data-loading="state.sessionCountLoading"></div>
        <v-data-table
          :headers="sessionCountTableData.headers"
          :items="sessionCountChart"
          item-value="name"
          density="comfortable"
          class="google-analytics-session-count-table"
          :data-loading="state.sessionCountLoading"
        >
          <template v-slot:item.sessions="{ item }">
            {{ formatNumber(item.sessions) }}
          </template>
          <template v-slot:item.change.sessions="{ item }">
            <span :class="item.change.sessions >= 0 ? 'success--text' : 'error--text'">
              {{ item.change.sessions >= 0 ? "▲" : "▼" }}
              {{ formatNumber(Math.abs(item.change.sessions) * 100) }}%
            </span>
          </template>
          <template v-slot:item.sessionPercent="{ item }">
            {{ formatNumber(item.sessionPercent * 100) }}%
          </template>
          <template v-slot:item.change.sessionPercent="{ item }">
            <span :class="item.change.sessionPercent >= 0 ? 'success--text' : 'error--text'">
              {{ item.change.sessionPercent >= 0 ? "▲" : "▼" }}
              {{ formatNumber(Math.abs(item.change.sessionPercent) * 100) }}%
            </span>
          </template>
          <template v-slot:item.eventCount="{ item }">
            {{ formatNumber(item.eventCount) }}
          </template>
          <template v-slot:item.change.eventCount="{ item }">
            <span :class="item.change.eventCount >= 0 ? 'success--text' : 'error--text'">
              {{ item.change.eventCount >= 0 ? "▲" : "▼" }}
              {{ formatNumber(Math.abs(item.change.eventCount) * 100) }}%
            </span>
          </template>
          <template v-slot:item.eventsPerSession="{ item }">
            {{ formatNumber(item.eventsPerSession) }}
          </template>
          <template v-slot:item.change.eventsPerSession="{ item }">
            <span :class="item.change.eventsPerSession >= 0 ? 'success--text' : 'error--text'">
              {{ item.change.eventsPerSession >= 0 ? "▲" : "▼" }}
              {{ formatNumber(Math.abs(item.change.eventsPerSession) * 100) }}%
            </span>
          </template>
        </v-data-table>
      </v-card>
    </v-col>
  </v-row>

  <!-- City Active Users and Monthly Event Count Charts -->
  <v-row class="page-break">
    <v-col
      cols="12"
      md="12"
      lg="12"
      xl="4"
      v-for="item in cityActiveUsersLineChartDataCities"
      :key="`${item.country}-${item.city}`"
    >
      <v-card class="w-full chart-card">
        <v-card-text>
          <ChartLazyLoader :loading="state.cityActiveUsersMonthlyLoading">
            <LineChart
              :series="
                cityActiveUsersChart.find((d) => d.key === `City Active Users: ${item.city}`)!.value
                  .series
              "
              :xAxis="
                cityActiveUsersChart.find((d) => d.key === `City Active Users: ${item.city}`)!.value
                  .xAxis
              "
              :title="`Active Users by City: ${item.city}`"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Monthly Event Count Charts -->
    <v-col
      cols="12"
      md="12"
      lg="12"
      xl="4"
      v-for="item in monthlyEventCountData"
      :key="item.eventName"
    >
      <v-card class="w-full chart-card">
        <v-card-text>
          <ChartLazyLoader :loading="state.monthlyEventCountLoading">
            <LineChart
              :series="
                monthlyEventCountChart.find(
                  (d) => d.key === `Monthly Event Count: ${item.eventName}`
                )!.value.series
              "
              :xAxis="
                monthlyEventCountChart.find(
                  (d) => d.key === `Monthly Event Count: ${item.eventName}`
                )!.value.xAxis
              "
              :title="`Monthly Event Count: ${item.eventName}`"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <v-row>
    <!-- Top Events Table -->
    <v-col cols="12">
      <v-card class="google-analytics-top-events-table-card" :title="`Top Events`">
        <v-data-table
          :headers="eventCountTableData.headers"
          :items="topEventsChart"
          item-value="name"
          :loading="eventCountTableData.loading"
          density="comfortable"
          class="google-analytics-top-events-table"
          :items-per-page="5"
          :data-loading="eventCountTableData.loading"
        >
          <template v-slot:item.eventCount="{ item }">
            {{ formatNumber(item.eventCount) }}
          </template>
          <template v-slot:item.change.eventCount="{ item }">
            <span :class="item.change.eventCount >= 0 ? 'success--text' : 'error--text'">
              {{ item.change.eventCount >= 0 ? "▲" : "▼" }}
              {{ formatNumber(Math.abs(item.change.eventCount) * 100) }}%
            </span>
          </template>
          <template v-slot:item.eventPercent="{ item }">
            {{ formatNumber(item.eventPercent * 100) }}%
          </template>
          <template v-slot:item.change.eventPercent="{ item }">
            <span :class="item.change.eventPercent >= 0 ? 'success--text' : 'error--text'">
              {{ item.change.eventPercent >= 0 ? "▲" : "▼" }}
              {{ formatNumber(Math.abs(item.change.eventCount) * 100) }}%
            </span>
          </template>
        </v-data-table>
      </v-card>
    </v-col>

    <!-- Monthly Device Active Users Charts -->
    <v-col
      cols="12"
      md="12"
      lg="12"
      xl="4"
      v-for="(item, index) in monthlyDeviceActiveUsersData"
      :key="item.device"
      :class="{ 'page-break': index === 0 }"
    >
      <v-card class="w-full chart-card">
        <v-card-text>
          <ChartLazyLoader :loading="state.monthlyDeviceActiveUsersLoading">
            <LineChart
              :series="
                monthlyDeviceActiveUsersChart.find(
                  (d) => d.key === `Monthly Device Active Users: ${item.device}`
                )!.value.series
              "
              :xAxis="
                monthlyDeviceActiveUsersChart.find(
                  (d) => d.key === `Monthly Device Active Users: ${item.device}`
                )!.value.xAxis
              "
              :title="`Monthly Device Active Users: ${item.device}`"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Organic Total Metrics Charts -->
    <v-col cols="12" md="12" lg="12" xl="4" v-for="item in ORGANIC_TOTAL_METRICS" :key="item">
      <v-card class="w-full chart-card">
        <v-card-text>
          <ChartLazyLoader :loading="state.organicTotalLoading">
            <LineChart
              :series="
                organicTotalChart.find((d) => d.key === `Organic Total: ${item}`)!.value.series
              "
              :xAxis="
                organicTotalChart.find((d) => d.key === `Organic Total: ${item}`)!.value.xAxis
              "
              :title="`Organic Total: ${item}`"
              class="no-axis-label-in-tooltip organic-total-line-chart"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Organic City Active Users Monthly Charts -->
    <v-col
      cols="12"
      md="12"
      lg="12"
      xl="4"
      v-for="item in organicCityActiveUsersMonthlyData"
      :key="item.city"
    >
      <v-card class="w-full chart-card">
        <v-card-text>
          <ChartLazyLoader :loading="state.organicCityActiveUsersMonthlyLoading">
            <LineChart
              :series="
                organicCityActiveUsersCharts.find(
                  (d) => d.key === `Organic Monthly Cities Active Users: ${item.city}`
                )!.value.series
              "
              :xAxis="
                organicCityActiveUsersCharts.find(
                  (d) => d.key === `Organic Monthly Cities Active Users: ${item.city}`
                )!.value.xAxis
              "
              :title="`Organic Monthly Cities Active Users: ${item.city}`"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Organic Monthly Device Active Users Charts -->
    <v-col
      cols="12"
      md="12"
      lg="12"
      xl="4"
      v-for="(item, index) in organicMonthlyDeviceActiveUsersData"
      :key="item.device"
      :class="{ 'page-break': index === 0 }"
    >
      <v-card class="w-full chart-card">
        <v-card-text>
          <ChartLazyLoader :loading="state.monthlyOrganicDeviceActiveUsersLoading">
            <LineChart
              :series="
                organicMonthlyDeviceCharts.find(
                  (d) => d.key === `Organic Monthly Device Active Users: ${item.device}`
                )!.value.series
              "
              :xAxis="
                organicMonthlyDeviceCharts.find(
                  (d) => d.key === `Organic Monthly Device Active Users: ${item.device}`
                )!.value.xAxis
              "
              :title="`Organic Monthly Device Active Users: ${item.device}`"
              class="no-axis-label-in-tooltip"
            />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
  <div
    :data="JSON.stringify(dataTotal)"
    class="tw:hidden!"
    source="Google Analytics"
    type="data-total"
  ></div>
</template>

<script setup lang="ts">
const props = defineProps<{
  account: IApp.IAccount;
  monthRange: IApp.MonthRange;
  comparedMonthRange: IApp.MonthRange;
  printMode: IApp.PrintMode;
  report?: IApp.IReport | null;
  requestId?: string;
  reportId?: string;
  onChangeSummary?: (summary: string) => void;
  mode?: IApp.PrintMode;
  showSummary: boolean;
}>();

import { Google, IApp, type IApiRequest } from "@/types";
import _ from "lodash";
import {
  formatNumber,
  formatSecond,
  formatToShortMonth,
  iterateMonths,
  makeAlert,
  store
} from "@/utils";
import ServiceLogo from "../Widgets/ServiceLogo.vue";
import { computed, onMounted, reactive, ref, watch } from "vue";
import GoogleAnalytics from "@/actions/GoogleAnalytics";
import LineChart, { type LineChartProps } from "../LineChart.vue";
import { useAuthStore } from "@/stores/auth";
import { v4 } from "uuid";
import ChartLazyLoader from "@/components/ChartLazyLoader.vue";

const authStore = useAuthStore();
const user = computed(() => authStore.user);
const componentUuid = ref<string>(v4());

const state = ref<{
  topViewedPagesDataLoading: boolean;
  totalLoading: boolean;
  sessionCountLoading: boolean;
  cityActiveUsersMonthlyLoading: boolean;
  eventCountLoading: boolean;
  monthlyEventCountLoading: boolean;
  monthlyDeviceActiveUsersLoading: boolean;
  organicTotalLoading: boolean;
  organicCityActiveUsersMonthlyLoading: boolean;
  monthlyOrganicDeviceActiveUsersLoading: boolean;
  selectedTotalItems: IApp.IGoogleAnalyticsAnalyticsTotalMetrics[];
  activeUsersByCitySelectedCities: number[];
  organicActiveUsersByCitySelectedCities: number[];
  eventCountByMonthSelectedEvents: number[];
  activeUsersByDeviceSelectedDevices: number[];
  organicTotalSelectedMetrics: number[];
  monthlyOrganicDeviceActiveUsersSelectedDevices: number[];
  isDataLoaded: boolean;
  numberLoadingFinished: number;
}>({
  topViewedPagesDataLoading: true,
  totalLoading: true,
  sessionCountLoading: true,
  cityActiveUsersMonthlyLoading: true,
  eventCountLoading: true,
  monthlyEventCountLoading: true,
  monthlyDeviceActiveUsersLoading: true,
  organicTotalLoading: true,
  monthlyOrganicDeviceActiveUsersLoading: true,
  organicCityActiveUsersMonthlyLoading: true,
  selectedTotalItems: [],
  activeUsersByCitySelectedCities: [],
  organicActiveUsersByCitySelectedCities: [],
  eventCountByMonthSelectedEvents: [],
  activeUsersByDeviceSelectedDevices: [],
  organicTotalSelectedMetrics: [],
  monthlyOrganicDeviceActiveUsersSelectedDevices: [],
  isDataLoaded: false,
  numberLoadingFinished: 0
});

const emit = defineEmits<{
  (e: "onChangeData", value: Array<{ key: string; value: [] }>): void;
  (e: "onChangeDataLoad", value: { total: number; loaded: number }): void;
}>();

const dataTotal = reactive<Array<{ key: string; value: [] }>>([]);

watch(
  dataTotal,
  (newDataTotal) => {
    emit("onChangeData", newDataTotal);
  },
  { immediate: true, deep: true }
);

const totalItems = ref([
  {
    label: "Total Users",
    value: IApp.IGoogleAnalyticsAnalyticsTotalMetrics.TotalUsers
  },
  {
    label: "New Users",
    value: IApp.IGoogleAnalyticsAnalyticsTotalMetrics.NewUsers
  },
  {
    label: "Sessions",
    value: IApp.IGoogleAnalyticsAnalyticsTotalMetrics.Sessions
  },
  {
    label: "Views",
    value: IApp.IGoogleAnalyticsAnalyticsTotalMetrics.ScreenPageViews
  },
  {
    label: "Views Per Session",
    value: IApp.IGoogleAnalyticsAnalyticsTotalMetrics.ScreenPageViewsPerSession
  },
  {
    label: "Event Count",
    value: IApp.IGoogleAnalyticsAnalyticsTotalMetrics.EventCount
  },
  {
    label: "Events Per Session",
    value: IApp.IGoogleAnalyticsAnalyticsTotalMetrics.EventsPerSession
  },
  {
    label: "Engaged Sessions",
    value: IApp.IGoogleAnalyticsAnalyticsTotalMetrics.EngagedSessions
  },
  {
    label: "Engagement Rate",
    value: IApp.IGoogleAnalyticsAnalyticsTotalMetrics.EngagementRate
  },
  {
    label: "Avg. Session Duration",
    value: IApp.IGoogleAnalyticsAnalyticsTotalMetrics.AverageSessionDuration
  }
]);

const TOTAL_METRICS = [
  Google.GoogleAnalyticsOrganicTotalMetrics.ActiveUsers,
  Google.GoogleAnalyticsOrganicTotalMetrics.NewUsers,
  Google.GoogleAnalyticsOrganicTotalMetrics.TotalUsers,
  Google.GoogleAnalyticsOrganicTotalMetrics.EngagementRate,
  Google.GoogleAnalyticsOrganicTotalMetrics.UserEngagementDuration,
  Google.GoogleAnalyticsOrganicTotalMetrics.EngagedSessions,
  Google.GoogleAnalyticsOrganicTotalMetrics.EventCount,
  Google.GoogleAnalyticsOrganicTotalMetrics.EventsPerSession,
  Google.GoogleAnalyticsOrganicTotalMetrics.Sessions
];

const ORGANIC_TOTAL_METRICS = [
  Google.GoogleAnalyticsOrganicTotalMetrics.ActiveUsers,
  Google.GoogleAnalyticsOrganicTotalMetrics.NewUsers,
  Google.GoogleAnalyticsOrganicTotalMetrics.TotalUsers,
  Google.GoogleAnalyticsOrganicTotalMetrics.EngagedSessions,
  Google.GoogleAnalyticsOrganicTotalMetrics.Sessions
];

const startMonth = computed(() => props.monthRange.start);
const endMonth = computed(() => props.monthRange.end);
const monthRangeString = computed(
  () => `${formatToShortMonth(startMonth.value)} - ${formatToShortMonth(endMonth.value)}`
);

const comparedStartMonth = computed(() => props.comparedMonthRange.start);
const comparedEndMonth = computed(() => props.comparedMonthRange.end);
const comparedMonthRangeString = computed(
  () =>
    `${formatToShortMonth(comparedStartMonth.value)} - ${formatToShortMonth(comparedEndMonth.value)}`
);
const comparedMonths = computed(() =>
  Array.from(iterateMonths(comparedStartMonth.value, comparedEndMonth.value))
);

const accountInfo = computed<IApp.IGoogleAnalyticsAccount>(() => props.account as any);

const totalAnalyticsData = ref<IApiRequest.GoogleAnalyticsTotalOutput>([]);
const comparedTotalAnalyticsData = ref<IApiRequest.GoogleAnalyticsTotalOutput>([]);

const pageViewsAnalyticsData = ref<IApiRequest.GoogleAnalyticsPageViewsOutput>([]);
const sessionCountAnalyticsData = ref<IApiRequest.GoogleAnalyticsSessionCountOutput>([]);
const cityActiveUsersMonthlyData = ref<IApiRequest.GoogleAnalyticsCityActiveUsersMonthlyOutput>([]);
const eventCountData = ref<IApiRequest.GoogleAnalyticsEventCountOutput>([]);
const monthlyEventCountData = ref<IApiRequest.GoogleAnalyticsMonthlyEventCountOutput>([]);
const monthlyDeviceActiveUsersData = ref<IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersOutput>(
  []
);
const comparedMonthlyDeviceActiveUsersData =
  ref<IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersOutput>([]);
const organicTotalData = ref<IApiRequest.GoogleAnalyticsOrganicTotalMonthlyOutput>([]);
const comparedOrganicTotalData = ref<IApiRequest.GoogleAnalyticsOrganicTotalMonthlyOutput>([]);
const organicCityActiveUsersMonthlyData =
  ref<IApiRequest.GoogleAnalyticsCityActiveUsersMonthlyOutput>([]);
const organicMonthlyDeviceActiveUsersData =
  ref<IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersOutput>([]);
const comparedOrganicMonthlyDeviceActiveUsersData =
  ref<IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersOutput>([]);

const generateTotalAnalyticsLineChartData = (
  key: keyof IApiRequest.GoogleAnalyticsTotalOutputItem["counts"]
): LineChartProps => {
  return {
    series: [
      {
        name: `${formatToShortMonth(comparedStartMonth.value)} - ${formatToShortMonth(comparedEndMonth.value)}`,
        data: comparedTotalAnalyticsData.value.map((item) => ({
          y: key === "engagementRate" ? item.counts[key] * 100 : item.counts[key],
          x: formatToShortMonth(item.month),
          meta: { month: item.month }
        }))
      },
      {
        name: `${formatToShortMonth(startMonth.value)} - ${formatToShortMonth(endMonth.value)}`,
        data: totalAnalyticsData.value.map((item) => ({
          y: key === "engagementRate" ? item.counts[key] * 100 : item.counts[key],
          x: formatToShortMonth(item.month),
          meta: { month: item.month }
        }))
      }
    ],
    xAxis: Array.from(iterateMonths(startMonth.value, endMonth.value)).map((item) =>
      formatToShortMonth(item, true)
    ),
    loading: false,
    unit: key === "engagementRate" ? "%" : undefined,
    formatter:
      key === "averageSessionDuration" ? (val: number) => formatSecond(val).value : undefined
  } as LineChartProps;
};

const setAllLoading = (loading: boolean) => {
  state.value.totalLoading = loading;
  state.value.topViewedPagesDataLoading = loading;
  state.value.sessionCountLoading = loading;
  state.value.cityActiveUsersMonthlyLoading = loading;
  state.value.eventCountLoading = loading;
  state.value.monthlyEventCountLoading = loading;
  state.value.monthlyDeviceActiveUsersLoading = loading;
  state.value.organicTotalLoading = loading;
  state.value.organicCityActiveUsersMonthlyLoading = loading;
  state.value.monthlyOrganicDeviceActiveUsersLoading = loading;
};

const fetchAllData = async (customerId: string, accountId: string) => {
  setAllLoading(true);

  const response = await GoogleAnalytics.getAllAnalyticsData(
    user.value?.id!,
    customerId,
    accountId,
    {
      monthRange: { start: startMonth.value, end: endMonth.value },
      comparedMonthRange: { start: comparedStartMonth.value, end: comparedEndMonth.value }
    }
  );

  if (response.status) {
    const d = response.data as Record<string, unknown>;

    if (d["total"]) totalAnalyticsData.value = d["total"] as typeof totalAnalyticsData.value;
    if (d["total-compared"])
      comparedTotalAnalyticsData.value = d[
        "total-compared"
      ] as typeof comparedTotalAnalyticsData.value;
    state.value.totalLoading = false;

    if (d["page-views"])
      pageViewsAnalyticsData.value = d["page-views"] as typeof pageViewsAnalyticsData.value;
    state.value.topViewedPagesDataLoading = false;

    if (d["session-count"])
      sessionCountAnalyticsData.value = d[
        "session-count"
      ] as typeof sessionCountAnalyticsData.value;
    state.value.sessionCountLoading = false;

    if (d["monthly-city-active-users"]) {
      cityActiveUsersMonthlyData.value = d[
        "monthly-city-active-users"
      ] as typeof cityActiveUsersMonthlyData.value;
      state.value.activeUsersByCitySelectedCities = _.range(
        Math.min(cityActiveUsersMonthlyData.value.length, 1)
      );
    }
    state.value.cityActiveUsersMonthlyLoading = false;

    if (d["event-count"]) eventCountData.value = d["event-count"] as typeof eventCountData.value;
    state.value.eventCountLoading = false;

    if (d["monthly-event-count"]) {
      monthlyEventCountData.value = d["monthly-event-count"] as typeof monthlyEventCountData.value;
      state.value.eventCountByMonthSelectedEvents = _.range(
        Math.min(monthlyEventCountData.value.length, 1)
      );
    }
    state.value.monthlyEventCountLoading = false;

    if (d["monthly-device-active-users"]) {
      monthlyDeviceActiveUsersData.value = d[
        "monthly-device-active-users"
      ] as typeof monthlyDeviceActiveUsersData.value;
      state.value.activeUsersByDeviceSelectedDevices = _.range(
        Math.min(monthlyDeviceActiveUsersData.value.length, 1)
      );
    }
    if (d["monthly-device-active-users-compared"])
      comparedMonthlyDeviceActiveUsersData.value = d[
        "monthly-device-active-users-compared"
      ] as typeof comparedMonthlyDeviceActiveUsersData.value;
    state.value.monthlyDeviceActiveUsersLoading = false;

    if (d["organic-total"]) {
      organicTotalData.value = d["organic-total"] as typeof organicTotalData.value;
      state.value.organicTotalSelectedMetrics = _.range(Math.min(TOTAL_METRICS.length, 1));
    }
    if (d["organic-total-compared"])
      comparedOrganicTotalData.value = d[
        "organic-total-compared"
      ] as typeof comparedOrganicTotalData.value;
    state.value.organicTotalLoading = false;

    if (d["organic-city-active-users"]) {
      organicCityActiveUsersMonthlyData.value = d[
        "organic-city-active-users"
      ] as typeof organicCityActiveUsersMonthlyData.value;
      state.value.organicActiveUsersByCitySelectedCities = _.range(
        Math.min(organicCityActiveUsersMonthlyData.value.length, 1)
      );
    }
    state.value.organicCityActiveUsersMonthlyLoading = false;

    if (d["organic-device-active-users"])
      organicMonthlyDeviceActiveUsersData.value = d[
        "organic-device-active-users"
      ] as typeof organicMonthlyDeviceActiveUsersData.value;
    if (d["organic-device-active-users-compared"])
      comparedOrganicMonthlyDeviceActiveUsersData.value = d[
        "organic-device-active-users-compared"
      ] as typeof comparedOrganicMonthlyDeviceActiveUsersData.value;
    if (d["organic-device-active-users"]) {
      state.value.monthlyOrganicDeviceActiveUsersSelectedDevices = _.range(
        Math.min(organicMonthlyDeviceActiveUsersData.value.length, 1)
      );
    }
    state.value.monthlyOrganicDeviceActiveUsersLoading = false;
  } else {
    makeAlert(response.message, "error");
    setAllLoading(false);
  }
};

const topViewedPagesTableData = computed(() => ({
  items: pageViewsAnalyticsData.value.map((item, index) => ({
    no: index + 1,
    pagePath: item.pagePath,
    pageTitle: item.pageTitle,
    views: item.curr.screenPageViews,
    viewsChange: item.compared?.screenPageViews
      ? (item.curr.screenPageViews - item.compared.screenPageViews) /
        Math.abs(item.compared?.screenPageViews)
      : 1
  })),
  headers: [
    { title: "No", key: "no" },
    { title: "Page path", key: "pagePath" },
    { title: "Page title", key: "pageTitle" },
    { title: "Views", key: "views" },
    { title: "% Δ", key: "viewsChange" }
  ]
}));

const sessionCountTableData = computed(() => ({
  items: sessionCountAnalyticsData.value.map((item, index) => ({
    no: index + 1,
    sessionSource: item.sessionSource,
    sessionSourceMedium: _.trim(_.last(_.split(item.sessionSourceMedium, "/"))),
    sessions: item.curr.sessions,
    sessionPercent: item.curr.sessions / item.curr.totalSessions,
    eventCount: item.curr.eventCount,
    eventsPerSession: item.curr.eventsPerSession,
    change: {
      sessions: item.compared.sessions
        ? (item.curr.sessions - item.compared.sessions) / Math.abs(item.compared.sessions)
        : 1,
      sessionPercent: item.compared.sessions
        ? (item.curr.sessions / item.curr.totalSessions -
            item.compared.sessions / item.compared.totalSessions) /
          Math.abs(item.compared.sessions / item.compared.totalSessions)
        : 1,
      eventCount: item.compared.eventCount
        ? (item.curr.eventCount - item.compared.eventCount) / Math.abs(item.compared.eventCount)
        : 1,
      eventsPerSession: item.compared.eventsPerSession
        ? (item.curr.eventsPerSession - item.compared.eventsPerSession) /
          Math.abs(item.compared.eventsPerSession)
        : 1
    }
  })),
  headers: [
    { title: "No", key: "no" },
    { title: "Session Source", key: "sessionSource" },
    { title: "Session Source Medium", key: "sessionSourceMedium" },
    { title: "Sessions", key: "sessions" },
    { title: "% Δ", key: "change.sessions" },
    { title: "% Session", key: "sessionPercent" },
    { title: "% Δ", key: "change.sessionPercent" },
    { title: "Event Count", key: "eventCount" },
    { title: "% Δ", key: "change.eventCount" },
    { title: "Events Per Session", key: "eventsPerSession" },
    { title: "% Δ", key: "change.eventsPerSession" }
  ]
}));

const makeCityActiveUsersLineChartData = (city: string, country: string): LineChartProps => ({
  series: [
    ..._(cityActiveUsersMonthlyData.value)
      .filter((item) => item.city === city && item.country == country)
      .map((item) => ({
        name: `${comparedMonthRangeString.value}`,
        data: item.compared.activeUsers.map((activeUser) => ({
          y: activeUser.value,
          x: formatToShortMonth(activeUser.month),
          meta: {
            ...activeUser,
            label: `${formatToShortMonth(activeUser.month)} ${item.city}`
          }
        }))
      }))
      .value(),
    ..._(cityActiveUsersMonthlyData.value)
      .filter((item) => item.city === city && item.country == country)
      .map((item) => ({
        name: `${monthRangeString.value}`,
        data: item.curr.activeUsers.map((activeUser) => ({
          y: activeUser.value,
          x: formatToShortMonth(activeUser.month),
          meta: {
            ...activeUser,
            label: `${formatToShortMonth(activeUser.month)} ${item.city}`
          }
        }))
      }))
      .value()
  ],
  title: `Active Users by City: ${city}`,
  xAxis: Array.from(iterateMonths(startMonth.value, endMonth.value)).map((item) =>
    formatToShortMonth(item, true)
  ),
  loading: state.value.cityActiveUsersMonthlyLoading
});

const cityActiveUsersLineChartDataCities = computed(() =>
  cityActiveUsersMonthlyData.value.map((item) => ({ city: item.city, country: item.country }))
);

const eventCountTableData = computed(() => ({
  items: eventCountData.value.map((item, index) => ({
    no: index + 1,
    eventName: item.eventName,
    eventCount: item.curr.eventCount,
    eventPercent: item.curr.eventCount / item.curr.totalEvents,
    change: {
      eventCount: item.compared.eventCount
        ? (item.curr.eventCount - item.compared.eventCount) / Math.abs(item.compared.eventCount)
        : 1,
      eventPercent: item.compared.eventCount
        ? (item.curr.eventCount / item.curr.totalEvents -
            item.compared.eventCount / item.compared.totalEvents) /
          Math.abs(item.compared.eventCount / item.compared.totalEvents)
        : 1
    }
  })),
  headers: [
    { title: "No", key: "no" },
    { title: "Event Name", key: "eventName" },
    { title: "Event Count", key: "eventCount" },
    { title: "% Δ", key: "change.eventCount" },
    { title: "% Event", key: "eventPercent" },
    { title: "% Δ", key: "change.eventPercent" }
  ],
  loading: state.value.eventCountLoading
}));

const makeMonthlyEventCountLineChartData = (eventName: string) => ({
  series: [
    ..._(monthlyEventCountData.value)
      .filter((item) => item.eventName === eventName)
      .map((item) => ({
        name: `${comparedMonthRangeString.value}`,
        data: item.compared.counts.map((count) => ({
          y: count.value,
          x: formatToShortMonth(count.month),
          meta: {
            ...count,
            label: `${formatToShortMonth(count.month)} ${item.eventName}`
          }
        }))
      }))
      .value(),
    ..._(monthlyEventCountData.value)
      .filter((item) => item.eventName === eventName)
      .map((item) => ({
        name: `${monthRangeString.value}`,
        data: item.curr.counts.map((count) => ({
          y: count.value,
          x: formatToShortMonth(count.month),
          meta: {
            ...count,
            label: `${formatToShortMonth(count.month)} ${item.eventName}`
          }
        }))
      }))
      .value()
  ],
  title: `Event Count by Month: ${eventName}`,
  xAxis: Array.from(iterateMonths(startMonth.value, endMonth.value)).map((item) =>
    formatToShortMonth(item, true)
  ),
  loading: state.value.monthlyEventCountLoading
});

const makeMonthlyDeviceActiveUsersLineChartData = (device: string) => ({
  series: [
    ..._(monthlyDeviceActiveUsersData.value)
      .filter((item) => item.device === device)
      .map((item) => ({
        name: `${comparedMonthRangeString.value}`,
        data: item.activeUsers.map((activeUser, index) => {
          const comparedMonth = comparedMonths.value[index];
          const compared = _.find(
            _.find(comparedMonthlyDeviceActiveUsersData.value, {
              device: item.device
            })?.activeUsers,
            { month: comparedMonth }
          );
          return {
            y: compared?.value ?? 0,
            x: formatToShortMonth(comparedMonth),
            meta: {
              ...activeUser,
              label: `${formatToShortMonth(comparedMonth)}`
            }
          };
        })
      }))
      .value(),
    ..._(monthlyDeviceActiveUsersData.value)
      .filter((item) => item.device === device)
      .map((item) => ({
        name: `${monthRangeString.value}`,
        data: item.activeUsers.map((activeUser) => ({
          y: activeUser.value,
          x: formatToShortMonth(activeUser.month),
          meta: {
            ...activeUser,
            label: `${formatToShortMonth(activeUser.month)}`
          }
        }))
      }))
      .value()
  ],
  title: `Active Users by ${deviceMap[device]}`,
  xAxis: Array.from(iterateMonths(startMonth.value, endMonth.value)).map((item) =>
    formatToShortMonth(item, true)
  ),
  loading: state.value.monthlyDeviceActiveUsersLoading
});

const metricLabelMap: Record<Google.GoogleAnalyticsOrganicTotalMetrics, string> = {
  [Google.GoogleAnalyticsOrganicTotalMetrics.ActiveUsers]: "Active Users",
  [Google.GoogleAnalyticsOrganicTotalMetrics.NewUsers]: "New Users",
  [Google.GoogleAnalyticsOrganicTotalMetrics.TotalUsers]: "Total Users",
  [Google.GoogleAnalyticsOrganicTotalMetrics.EngagementRate]: "Engagement Rate",
  [Google.GoogleAnalyticsOrganicTotalMetrics.UserEngagementDuration]: "User Engagement Duration",
  [Google.GoogleAnalyticsOrganicTotalMetrics.EngagedSessions]: "Engaged Sessions",
  [Google.GoogleAnalyticsOrganicTotalMetrics.EventCount]: "Event Count",
  [Google.GoogleAnalyticsOrganicTotalMetrics.EventsPerSession]: "Events Per Session",
  [Google.GoogleAnalyticsOrganicTotalMetrics.Sessions]: "Sessions"
};

const deviceMap: Record<string, string> = {
  desktop: "Desktop",
  mobile: "Mobile",
  tablet: "Tablet",
  "smart tv": "Smart TV"
};

const makeOrganicTotalLineChartData = (metric: Google.GoogleAnalyticsOrganicTotalMetrics) => ({
  series: [
    ..._(comparedOrganicTotalData.value)
      .filter((item) => _.includes(ORGANIC_TOTAL_METRICS, item.metric) && item.metric === metric)
      .map((item) => ({
        name: `${comparedMonthRangeString.value}`,
        data: item.count.map((countItem) => ({
          y: countItem.value,
          x: formatToShortMonth(countItem.month),
          meta: {
            ...countItem,
            label: `${formatToShortMonth(countItem.month)} ${metricLabelMap[item.metric]}`
          }
        }))
      }))
      .value(),
    ..._(organicTotalData.value)
      .filter((item) => _.includes(ORGANIC_TOTAL_METRICS, item.metric) && item.metric === metric)
      .map((item) => ({
        name: `${monthRangeString.value}`,
        data: item.count.map((countItem) => ({
          y: countItem.value,
          x: formatToShortMonth(countItem.month),
          meta: {
            ...countItem,
            label: `${formatToShortMonth(countItem.month)} ${metricLabelMap[item.metric]}`
          }
        }))
      }))
      .value()
  ],
  title: `Organic ${metricLabelMap[metric]}`,
  xAxis: Array.from(iterateMonths(startMonth.value, endMonth.value)).map((item) =>
    formatToShortMonth(item, true)
  ),
  loading: state.value.monthlyDeviceActiveUsersLoading
});

const makeMonthlyOrganicDeviceActiveUsersLineChartData = (device: string) => ({
  series: [
    ..._(organicMonthlyDeviceActiveUsersData.value)
      .filter({ device })
      .map((item) => {
        const compared = _.find(comparedOrganicMonthlyDeviceActiveUsersData.value, {
          device: item.device
        });
        return {
          name: `${comparedMonthRangeString.value}`,
          data: item.activeUsers.map((__, index) => ({
            y: compared?.activeUsers[index].value ?? 0,
            x: formatToShortMonth(comparedMonths.value[index]),
            meta: {
              ...(compared?.activeUsers[index] ?? {}),
              label: `${formatToShortMonth(comparedMonths.value[index])}`
            }
          }))
        };
      })
      .value(),
    ..._(organicMonthlyDeviceActiveUsersData.value)
      .filter({ device })
      .map((item) => ({
        name: `${monthRangeString.value}`,
        data: item.activeUsers.map((activeUser) => ({
          y: activeUser.value,
          x: formatToShortMonth(activeUser.month),
          meta: {
            ...activeUser,
            label: `${formatToShortMonth(activeUser.month)}`
          }
        }))
      }))
      .value()
  ],
  title: `Organic Active Users by ${deviceMap[device]}`,
  xAxis: Array.from(iterateMonths(startMonth.value, endMonth.value)).map((item) =>
    formatToShortMonth(item, true)
  ),
  loading: state.value.monthlyOrganicDeviceActiveUsersLoading
});

const makeOrganicMonthlyCitiesActiveUsersLineChartData = (city: string) => ({
  series: [
    ..._(organicCityActiveUsersMonthlyData.value)
      .filter({ city })
      .map((item) => ({
        name: `${comparedMonthRangeString.value}`,
        data: item.compared.activeUsers.map((activeUser) => ({
          y: activeUser.value,
          x: formatToShortMonth(activeUser.month),
          meta: {
            ...activeUser,
            label: `${formatToShortMonth(activeUser.month)} ${item.city}`
          }
        }))
      }))
      .value(),
    ..._(organicCityActiveUsersMonthlyData.value)
      .filter({ city })
      .map((item) => ({
        name: `${monthRangeString.value}`,
        data: item.curr.activeUsers.map((activeUser) => ({
          y: activeUser.value,
          x: formatToShortMonth(activeUser.month),
          meta: {
            ...activeUser,
            label: `${formatToShortMonth(activeUser.month)} ${item.city}`
          }
        }))
      }))
      .value()
  ],
  title: `Organic Active Users by City: ${deviceMap[city] ?? city}`,
  xAxis: Array.from(iterateMonths(startMonth.value, endMonth.value)).map((item) =>
    formatToShortMonth(item, true)
  ),
  loading: state.value.organicCityActiveUsersMonthlyLoading
});

const totalAnalyticsChartData = computed(() =>
  totalItems.value.map((item) => ({
    key: item.label,
    value: store(dataTotal, item.label, generateTotalAnalyticsLineChartData(item.value))
  }))
);
const topViewedPagesChart = computed(() =>
  store(dataTotal, "Top viewed pages", topViewedPagesTableData.value.items)
);
const sessionCountChart = computed(() =>
  store(dataTotal, "Top Event/Traffic Sources", sessionCountTableData.value.items)
);
const cityActiveUsersChart = computed(() =>
  cityActiveUsersMonthlyData.value.map((item) => ({
    key: `City Active Users: ${item.city}`,
    value: store(
      dataTotal,
      `City Active Users: ${item.city}`,
      makeCityActiveUsersLineChartData(item.city, item.country)
    )
  }))
);
const monthlyEventCountChart = computed(() =>
  monthlyEventCountData.value.map((item) => ({
    key: `Monthly Event Count: ${item.eventName}`,
    value: store(
      dataTotal,
      `Monthly Event Count: ${item.eventName}`,
      makeMonthlyEventCountLineChartData(item.eventName)
    )
  }))
);
const topEventsChart = computed(() =>
  store(dataTotal, "Top Events", eventCountTableData.value.items)
);
const monthlyDeviceActiveUsersChart = computed(() =>
  monthlyDeviceActiveUsersData.value.map((item) => ({
    key: `Monthly Device Active Users: ${item.device}`,
    value: store(
      dataTotal,
      `Monthly Device Active Users: ${item.device}`,
      makeMonthlyDeviceActiveUsersLineChartData(item.device)
    )
  }))
);
const organicTotalChart = computed(() =>
  ORGANIC_TOTAL_METRICS.map((metric) => ({
    key: `Organic Total: ${metric}`,
    value: store(dataTotal, `Organic Total: ${metric}`, makeOrganicTotalLineChartData(metric))
  }))
);
const organicCityActiveUsersCharts = computed(() =>
  organicCityActiveUsersMonthlyData.value.map((item) => ({
    key: `Organic Monthly Cities Active Users: ${item.city}`,
    value: store(
      dataTotal,
      `Organic Monthly Cities Active Users: ${item.city}`,
      makeOrganicMonthlyCitiesActiveUsersLineChartData(item.city)
    )
  }))
);
const organicMonthlyDeviceCharts = computed(() =>
  organicMonthlyDeviceActiveUsersData.value.map((item) => ({
    key: `Organic Monthly Device Active Users: ${item.device}`,
    value: store(
      dataTotal,
      `Organic Monthly Device Active Users: ${item.device}`,
      makeMonthlyOrganicDeviceActiveUsersLineChartData(item.device)
    )
  }))
);

const reFetch = _.debounce(async () => {
  const now = new Date().getTime();

  if (now - (_.get(window, `lastFetched.${componentUuid.value}`) ?? 0) > 500) {
    _.set(window, `lastFetched.${componentUuid.value}`, now);
    try {
      state.value.numberLoadingFinished = 0;
      await fetchAllData(accountInfo.value.customerId, accountInfo.value.account);
      state.value.numberLoadingFinished = 10;
      emit("onChangeDataLoad", { total: 10, loaded: 10 });
      state.value.isDataLoaded = true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err: unknown) {
      makeAlert("Failed to load Google Analytics data", "error");
    }
  }
}, 500);

onMounted(() => {
  state.value.isDataLoaded = false;
  reFetch();
});
watch([startMonth, endMonth, comparedStartMonth, comparedEndMonth], () => {
  reFetch();
});
</script>

<style>
.organic-total-line-chart .apexcharts-tooltip-series-group.apexcharts-active {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  height: 25px !important;
}

.no-detail .v-input__details {
  display: none;
}

.v-slide-group__content {
  justify-content: center;
}
</style>
